import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import '../styles/ImportCandidates.css';

const ImportCandidates = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [statusConfig, setStatusConfig] = useState(null);

  React.useEffect(() => {
    fetchDistricts();
    fetchStatusConfig();
  }, []);

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const snapshot = await getDocs(districtsRef);
      
      const districtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsList);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchStatusConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'systemConfig', 'statusOptions'));
      if (configDoc.exists()) {
        setStatusConfig(configDoc.data());
      }
    } catch (error) {
      console.error('Error fetching status config:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResults(null);
    } else {
      alert('Please select a valid CSV file.');
      e.target.value = '';
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const candidates = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = line.split(',').map(value => value.trim().replace(/"/g, ''));
        const candidate = {};
        
        headers.forEach((header, index) => {
          candidate[header] = values[index] || '';
        });
        
        candidates.push(candidate);
      }
    }

    return candidates;
  };

  // Comprehensive duplicate detection function
  const checkForDuplicates = async (candidateData) => {
    try {
      const candidatesRef = collection(db, 'candidates');
      
      // Strategy 1: Exact email match (most reliable)
      if (candidateData.email) {
        const emailQuery = query(candidatesRef, where('email', '==', candidateData.email.toLowerCase()));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          const existingCandidate = emailSnapshot.docs[0].data();
          return {
            isDuplicate: true,
            matchType: 'email',
            existingCandidate: {
              name: `${existingCandidate.firstName} ${existingCandidate.lastName}`,
              email: existingCandidate.email,
              id: emailSnapshot.docs[0].id
            },
            confidence: 'high'
          };
        }
      }

      // Strategy 2: Get all candidates for name-based matching
      const allCandidatesSnapshot = await getDocs(candidatesRef);
      const existingCandidates = allCandidatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Strategy 3: Exact name match (first + last)
      const exactNameMatches = existingCandidates.filter(existing => 
        existing.firstName?.toLowerCase() === candidateData.firstName?.toLowerCase() &&
        existing.lastName?.toLowerCase() === candidateData.lastName?.toLowerCase()
      );

      if (exactNameMatches.length > 0) {
        return {
          isDuplicate: true,
          matchType: 'exact_name',
          existingCandidate: {
            name: `${exactNameMatches[0].firstName} ${exactNameMatches[0].lastName}`,
            email: exactNameMatches[0].email,
            id: exactNameMatches[0].id
          },
          confidence: 'high'
        };
      }

      // Strategy 4: Name + phone match
      if (candidateData.phone) {
        const cleanPhone = candidateData.phone.replace(/\D/g, ''); // Remove non-digits
        const phoneMatches = existingCandidates.filter(existing => {
          const existingPhone = existing.phone?.replace(/\D/g, '');
          return existingPhone && existingPhone === cleanPhone &&
                 existing.firstName?.toLowerCase() === candidateData.firstName?.toLowerCase();
        });

        if (phoneMatches.length > 0) {
          return {
            isDuplicate: true,
            matchType: 'name_phone',
            existingCandidate: {
              name: `${phoneMatches[0].firstName} ${phoneMatches[0].lastName}`,
              email: phoneMatches[0].email,
              phone: phoneMatches[0].phone,
              id: phoneMatches[0].id
            },
            confidence: 'high'
          };
        }
      }

      // Strategy 5: Fuzzy name matching (similar names)
      const fuzzyMatches = existingCandidates.filter(existing => {
        const firstNameSimilarity = calculateSimilarity(
          candidateData.firstName?.toLowerCase() || '',
          existing.firstName?.toLowerCase() || ''
        );
        const lastNameSimilarity = calculateSimilarity(
          candidateData.lastName?.toLowerCase() || '',
          existing.lastName?.toLowerCase() || ''
        );
        
        // Consider it a potential duplicate if both names are very similar
        return firstNameSimilarity > 0.8 && lastNameSimilarity > 0.8;
      });

      if (fuzzyMatches.length > 0) {
        return {
          isDuplicate: true,
          matchType: 'similar_name',
          existingCandidate: {
            name: `${fuzzyMatches[0].firstName} ${fuzzyMatches[0].lastName}`,
            email: fuzzyMatches[0].email,
            id: fuzzyMatches[0].id
          },
          confidence: 'medium'
        };
      }

      // Strategy 6: Middle name variations (John Michael Smith vs John M. Smith)
      if (candidateData.middleName) {
        const middleNameMatches = existingCandidates.filter(existing => {
          const sameFirstLast = 
            existing.firstName?.toLowerCase() === candidateData.firstName?.toLowerCase() &&
            existing.lastName?.toLowerCase() === candidateData.lastName?.toLowerCase();
          
          if (!sameFirstLast) return false;

          // Check if one has full middle name and other has initial
          const candidateMiddle = candidateData.middleName.toLowerCase();
          const existingMiddle = existing.middleName?.toLowerCase() || '';
          
          return (candidateMiddle.charAt(0) === existingMiddle.charAt(0)) ||
                 (candidateMiddle === existingMiddle.charAt(0)) ||
                 (existingMiddle === candidateMiddle.charAt(0));
        });

        if (middleNameMatches.length > 0) {
          return {
            isDuplicate: true,
            matchType: 'name_middle_variation',
            existingCandidate: {
              name: `${middleNameMatches[0].firstName} ${middleNameMatches[0].middleName || ''} ${middleNameMatches[0].lastName}`.trim(),
              email: middleNameMatches[0].email,
              id: middleNameMatches[0].id
            },
            confidence: 'medium'
          };
        }
      }

      return { isDuplicate: false };

    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return { isDuplicate: false, error: 'Error checking duplicates' };
    }
  };

  // Helper function to calculate string similarity (Levenshtein distance)
  const calculateSimilarity = (str1, str2) => {
    if (str1.length === 0) return str2.length === 0 ? 1 : 0;
    if (str2.length === 0) return 0;

    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    const maxLength = Math.max(str1.length, str2.length);
    return (maxLength - matrix[str2.length][str1.length]) / maxLength;
  };

  const validateCandidate = (candidate) => {
    const errors = [];
    
    if (!candidate.firstName) errors.push('First name is required');
    if (!candidate.lastName) errors.push('Last name is required');
    if (!candidate.email) errors.push('Email is required');
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (candidate.email && !emailRegex.test(candidate.email)) {
      errors.push('Invalid email format');
    }

    // Validate conference status using dynamic config
    if (candidate.conferenceStatus) {
      let validStatuses = ['lay', 'clm', 'cmc', 'supply-pastor', 'local-pastor', 'other-fellowship', 'other']; // fallback
      
      if (statusConfig && statusConfig.conferenceStatuses) {
        validStatuses = statusConfig.conferenceStatuses
          .filter(status => status.enabled)
          .map(status => status.id);
      }
      
      if (!validStatuses.includes(candidate.conferenceStatus)) {
        errors.push(`Invalid conference status. Valid options: ${validStatuses.join(', ')}`);
      }
    }

    // Validate process stage using dynamic config
    if (candidate.processStage) {
      let validStages = ['inquiring', 'certified', 'full-time', 'part-time', 'completed', 'discontinued']; // fallback
      
      if (statusConfig && statusConfig.processStages) {
        validStages = statusConfig.processStages
          .filter(stage => stage.enabled)
          .map(stage => stage.id);
      }
      
      if (!validStages.includes(candidate.processStage)) {
        errors.push(`Invalid process stage. Valid options: ${validStages.join(', ')}`);
      }
    }

    return errors;
  };

  const handleImport = async () => {
    if (!file) {
      alert('Please select a CSV file first.');
      return;
    }

    setImporting(true);
    
    try {
      const csvText = await file.text();
      const candidatesData = parseCSV(csvText);
      
      const importResults = {
        total: candidatesData.length,
        successful: 0,
        failed: 0,
        duplicates: 0,
        errors: []
      };

      for (let i = 0; i < candidatesData.length; i++) {
        const candidateData = candidatesData[i];
        const errors = validateCandidate(candidateData);
        
        if (errors.length > 0) {
          importResults.failed++;
          importResults.errors.push({
            row: i + 2, // +2 because of header row and 0-based index
            candidate: `${candidateData.firstName} ${candidateData.lastName}`,
            errors: errors
          });
          continue;
        }

        // Check for duplicates before importing
        const duplicateCheck = await checkForDuplicates(candidateData);
        
        if (duplicateCheck.isDuplicate) {
          importResults.duplicates++;
          importResults.failed++;
          
          const duplicateMessage = `Duplicate candidate detected (${duplicateCheck.matchType} match, ${duplicateCheck.confidence} confidence)`;
          const existingInfo = `Existing: ${duplicateCheck.existingCandidate.name}${duplicateCheck.existingCandidate.email ? ` (${duplicateCheck.existingCandidate.email})` : ''}`;
          
          importResults.errors.push({
            row: i + 2,
            candidate: `${candidateData.firstName} ${candidateData.lastName}`,
            errors: [duplicateMessage, existingInfo],
            type: 'duplicate',
            matchType: duplicateCheck.matchType,
            confidence: duplicateCheck.confidence,
            existingCandidate: duplicateCheck.existingCandidate
          });
          continue;
        }

        try {
          // Prepare candidate data with defaults
          const candidate = {
            firstName: candidateData.firstName,
            middleName: candidateData.middleName || '',
            lastName: candidateData.lastName,
            email: candidateData.email,
            phone: candidateData.phone || '',
            address: candidateData.address || '',
            city: candidateData.city || '',
            state: candidateData.state || '',
            zipCode: candidateData.zipCode || '',
            conferenceStatus: candidateData.conferenceStatus || 'lay',
            processStage: candidateData.processStage || 'inquiring',
            district: candidateData.district || '',
            homeChurch: candidateData.homeChurch || '',
            currentAppointment: candidateData.currentAppointment || '',
            mentor: candidateData.mentor || '',
            dateOfBirth: candidateData.dateOfBirth || '',
            gender: candidateData.gender || '',
            ethnicity: candidateData.ethnicity || '',
            language: candidateData.language || '',
            emergencyContact: candidateData.emergencyContact || '',
            emergencyPhone: candidateData.emergencyPhone || '',
            education: candidateData.education || '',
            previousExperience: candidateData.previousExperience || '',
            notes: candidateData.notes || '',
            deleted: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            courseOfStudy: {
              courses: [],
              completedCredits: 0,
              totalCredits: 0
            },
            transcripts: [],
            actionReports: []
          };

          await addDoc(collection(db, 'candidates'), candidate);
          importResults.successful++;
        } catch (error) {
          console.error('Error adding candidate:', error);
          importResults.failed++;
          importResults.errors.push({
            row: i + 2,
            candidate: `${candidateData.firstName} ${candidateData.lastName}`,
            errors: ['Database error: ' + error.message]
          });
        }
      }

      setResults(importResults);
    } catch (error) {
      console.error('Error importing candidates:', error);
      alert('Error reading CSV file. Please check the file format.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'firstName',
      'middleName',
      'lastName', 
      'email',
      'phone',
      'address',
      'city',
      'state',
      'zipCode',
      'conferenceStatus',
      'processStage',
      'district',
      'homeChurch',
      'currentAppointment',
      'mentor',
      'dateOfBirth',
      'gender',
      'ethnicity',
      'language',
      'emergencyContact',
      'emergencyPhone',
      'education',
      'previousExperience',
      'notes'
    ];

    const sampleData = [
      'John',
      'Michael',
      'Doe',
      'john.doe@email.com',
      '555-123-4567',
      '123 Main St',
      'Anytown',
      'TX',
      '12345',
      'lay',
      'inquiring',
      'North District',
      'Grace UMC',
      'First UMC',
      'Rev. Jane Smith',
      '1980-01-15',
      'male',
      'caucasian',
      'english',
      'Jane Doe',
      '555-987-6543',
      'Bachelor of Arts in Theology',
      'Youth Pastor for 3 years',
      'Interested in ordained ministry'
    ];

    const csvContent = headers.join(',') + '\n' + sampleData.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="import-candidates-container">
      <div className="import-header">
        <h1>Import Candidates</h1>
        <button 
          onClick={() => navigate('/candidates')}
          className="back-btn"
        >
          Back to Candidates
        </button>
      </div>

      <div className="import-content">
        <div className="import-instructions">
          <h2>Import Instructions</h2>
          <div className="instructions-content">
            <h3>CSV Format Requirements:</h3>
            <ul>
              <li><strong>Required fields:</strong> firstName, lastName, email</li>
              <li><strong>Conference Status options:</strong> {
                statusConfig && statusConfig.conferenceStatuses ? 
                  statusConfig.conferenceStatuses
                    .filter(status => status.enabled)
                    .map(status => status.id)
                    .join(', ') :
                  'lay, clm, cmc, supply-pastor, local-pastor, other-fellowship, other'
              }</li>
              <li><strong>Process Stage options:</strong> {
                statusConfig && statusConfig.processStages ? 
                  statusConfig.processStages
                    .filter(stage => stage.enabled)
                    .map(stage => stage.id)
                    .join(', ') :
                  'inquiring, certified, full-time, part-time, completed, discontinued'
              }</li>
              <li><strong>Date format:</strong> YYYY-MM-DD (e.g., 1980-01-15)</li>
              <li><strong>Available Districts:</strong> {districts.map(d => d.name).join(', ')}</li>
            </ul>
            
            <div className="template-section">
              <h3>Download Template:</h3>
              <p>Use our template to ensure proper formatting:</p>
              <button onClick={downloadTemplate} className="template-btn">
                Download CSV Template
              </button>
            </div>
          </div>
        </div>

        <div className="import-form">
          <h2>Upload CSV File</h2>
          <div className="file-upload-section">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="file-input"
            />
            {file && (
              <div className="file-info">
                <p><strong>Selected file:</strong> {file.name}</p>
                <p><strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB</p>
              </div>
            )}
          </div>

          <div className="import-actions">
            <button 
              onClick={handleImport}
              disabled={!file || importing}
              className="import-btn"
            >
              {importing ? 'Importing...' : 'Import Candidates'}
            </button>
          </div>
        </div>

        {results && (
          <div className="import-results">
            <h2>Import Results</h2>
            <div className="results-summary">
              <div className="result-stat success">
                <span className="stat-number">{results.successful}</span>
                <span className="stat-label">Successful</span>
              </div>
              <div className="result-stat error">
                <span className="stat-number">{results.failed}</span>
                <span className="stat-label">Failed</span>
              </div>
              {results.duplicates > 0 && (
                <div className="result-stat duplicate">
                  <span className="stat-number">{results.duplicates}</span>
                  <span className="stat-label">Duplicates</span>
                </div>
              )}
              <div className="result-stat total">
                <span className="stat-number">{results.total}</span>
                <span className="stat-label">Total</span>
              </div>
            </div>

            {results.errors.length > 0 && (
              <div className="error-details">
                <h3>Import Errors:</h3>
                <div className="error-list">
                  {results.errors.map((error, index) => (
                    <div key={index} className="error-item">
                      <strong>Row {error.row} ({error.candidate}):</strong>
                      <ul>
                        {error.errors.map((err, errIndex) => (
                          <li key={errIndex}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="results-actions">
              <button 
                onClick={() => navigate('/candidates')}
                className="view-candidates-btn"
              >
                View All Candidates
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportCandidates;
